import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Upload,
  X,
  Send,
  Phone as PhoneIcon,
  Loader2,
  FileText,
  MessageCircle,
} from "lucide-react";
import { whatsappUrl } from "@/components/site/WhatsAppFloat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_FILE_MB = 8;
const MAX_TOTAL_MB = 20;

const typesProjet = [
  "Maison individuelle",
  "Extension",
  "Surélévation",
  "Rénovation avec PC",
  "Villa avec piscine",
  "Local professionnel",
  "Autre",
];

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const res = reader.result || "";
      const b64 = typeof res === "string" ? res.split(",")[1] || "" : "";
      resolve(b64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function ContactForm() {
  const [loading, setLoading] = useState(false);
  const [callbackLoading, setCallbackLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    nom: "",
    telephone: "",
    email: "",
    type_projet: "",
    message: "",
  });

  const [callback, setCallback] = useState({
    nom: "",
    telephone: "",
  });

  const onChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const onFileSelect = (e) => {
    const list = Array.from(e.target.files || []);
    const accepted = [];
    let total = files.reduce((acc, f) => acc + f.size, 0);
    for (const f of list) {
      if (f.size > MAX_FILE_MB * 1024 * 1024) {
        toast.error(`${f.name} dépasse ${MAX_FILE_MB} Mo`);
        continue;
      }
      if (total + f.size > MAX_TOTAL_MB * 1024 * 1024) {
        toast.error(`Taille totale > ${MAX_TOTAL_MB} Mo`);
        break;
      }
      total += f.size;
      accepted.push(f);
    }
    setFiles((prev) => [...prev, ...accepted]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (idx) =>
    setFiles((prev) => prev.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.telephone || !form.email) {
      toast.error("Merci de renseigner nom, téléphone et email.");
      return;
    }
    setLoading(true);
    try {
      const encoded = await Promise.all(
        files.map(async (f) => ({
          name: f.name,
          type: f.type || "application/octet-stream",
          size: f.size,
          data: await fileToBase64(f),
        }))
      );
      await axios.post(`${API}/leads`, {
        ...form,
        files: encoded,
        source: "devis",
        page_origine: typeof window !== "undefined" ? window.location.pathname : null,
      });
      toast.success(
        "Votre demande a bien été envoyée. Nous revenons vers vous rapidement."
      );
      setForm({
        nom: "",
        telephone: "",
        email: "",
        type_projet: "",
        message: "",
      });
      setFiles([]);
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Impossible d'envoyer le formulaire. Réessayez.";
      toast.error(typeof msg === "string" ? msg : "Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  const submitCallback = async (e) => {
    e.preventDefault();
    if (!callback.nom || !callback.telephone) {
      toast.error("Merci de renseigner votre nom et téléphone.");
      return;
    }
    setCallbackLoading(true);
    try {
      await axios.post(`${API}/leads/callback`, {
        ...callback,
        page_origine: typeof window !== "undefined" ? window.location.pathname : null,
      });
      toast.success("Nous vous rappelons sous 24h ouvrées.");
      setCallback({ nom: "", telephone: "" });
    } catch (err) {
      console.error(err);
      toast.error("Impossible d'enregistrer la demande. Réessayez.");
    } finally {
      setCallbackLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-secondary border-t border-border"
    >
      <div className="container-wide grid gap-16 lg:grid-cols-12">
        {/* Left: intro + callback */}
        <div className="lg:col-span-5 space-y-10 reveal">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="rule" />
              <span className="overline">Parlons de votre projet</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-medium leading-tight text-primary">
              Chaque projet est{" "}
              <span className="italic font-light">unique</span>.
            </h2>
            <p className="mt-5 text-base text-muted-foreground">
              Demandez votre devis personnalisé, gratuit et sans engagement.
              Vous préférez parler de vive voix ? Demandez un rappel ou
              contactez-nous directement sur WhatsApp.
            </p>

            <a
              href={whatsappUrl()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-link"
              className="mt-6 inline-flex items-center gap-3 px-5 py-3.5 bg-[#25D366] text-white text-sm font-medium tracking-wide rounded-full hover:bg-[#1ebd5a] transition-all duration-300 hover:-translate-y-0.5"
            >
              <MessageCircle size={18} strokeWidth={1.8} />
              Discuter sur WhatsApp
            </a>
          </div>

          <form
            id="callback"
            onSubmit={submitCallback}
            data-testid="callback-form"
            className="bg-white border border-border p-8"
          >
            <h3 className="font-display text-2xl text-primary mb-1">
              Être rappelé sous 24h
            </h3>
            <p className="text-xs text-muted-foreground mb-6">
              Un conseiller vous rappelle dans les 4h ouvrées — depuis Saint-Denis
              à Saint-Philippe, nous couvrons toute La Réunion.
            </p>
            <div className="space-y-5">
              <div>
                <Label
                  htmlFor="cb-nom"
                  className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground"
                >
                  Nom
                </Label>
                <Input
                  id="cb-nom"
                  data-testid="callback-input-nom"
                  value={callback.nom}
                  onChange={(e) =>
                    setCallback({ ...callback, nom: e.target.value })
                  }
                  required
                  className="mt-2 rounded-none border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary"
                />
              </div>
              <div>
                <Label
                  htmlFor="cb-tel"
                  className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground"
                >
                  Téléphone
                </Label>
                <Input
                  id="cb-tel"
                  type="tel"
                  data-testid="callback-input-tel"
                  value={callback.telephone}
                  onChange={(e) =>
                    setCallback({ ...callback, telephone: e.target.value })
                  }
                  required
                  className="mt-2 rounded-none border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary"
                />
              </div>
              <button
                type="submit"
                disabled={callbackLoading}
                data-testid="callback-submit"
                className="btn-primary w-full disabled:opacity-60"
              >
                {callbackLoading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <PhoneIcon size={16} strokeWidth={1.6} />
                )}
                {callbackLoading ? "Envoi..." : "Être rappelé"}
              </button>
            </div>
          </form>
        </div>

        {/* Right: main form */}
        <div className="lg:col-span-7 reveal">
          <form
            onSubmit={submit}
            data-testid="quote-form"
            className="bg-white border border-border p-8 md:p-10 space-y-8"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <Field
                label="Nom complet"
                name="nom"
                value={form.nom}
                onChange={onChange}
                required
                testId="quote-input-nom"
              />
              <Field
                label="Téléphone"
                name="telephone"
                value={form.telephone}
                type="tel"
                onChange={onChange}
                required
                testId="quote-input-tel"
              />
              <Field
                label="Email"
                name="email"
                value={form.email}
                type="email"
                onChange={onChange}
                required
                testId="quote-input-email"
              />
              <div>
                <Label className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                  Type de projet
                </Label>
                <Select
                  value={form.type_projet}
                  onValueChange={(v) => setForm({ ...form, type_projet: v })}
                >
                  <SelectTrigger
                    data-testid="quote-select-type"
                    className="mt-2 rounded-none border-0 border-b border-border bg-transparent px-0 focus:ring-0 focus:border-primary h-auto py-2 text-base"
                  >
                    <SelectValue placeholder="Sélectionnez" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {typesProjet.map((t) => (
                      <SelectItem key={t} value={t} className="rounded-none">
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                Message
              </Label>
              <Textarea
                name="message"
                value={form.message}
                onChange={onChange}
                rows={4}
                data-testid="quote-input-message"
                placeholder="Décrivez votre projet, votre terrain, vos contraintes…"
                className="mt-2 rounded-none border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary resize-none"
              />
            </div>

            {/* File upload */}
            <div>
              <Label className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                Documents (optionnel)
              </Label>
              <div
                onClick={() => fileInputRef.current?.click()}
                data-testid="quote-file-dropzone"
                className="mt-3 cursor-pointer border-2 border-dashed border-border bg-muted/30 p-8 text-center hover:border-primary transition-colors"
              >
                <Upload
                  size={22}
                  strokeWidth={1.5}
                  className="mx-auto text-primary"
                />
                <p className="mt-3 text-sm text-primary/85">
                  Cliquez pour ajouter des fichiers
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Plans, PLU, relevés · max {MAX_FILE_MB} Mo par fichier ·{" "}
                  {MAX_TOTAL_MB} Mo au total
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={onFileSelect}
                  className="hidden"
                  data-testid="quote-file-input"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.dwg,.zip"
                />
              </div>

              {files.length > 0 && (
                <ul
                  data-testid="quote-file-list"
                  className="mt-4 divide-y divide-border border border-border"
                >
                  {files.map((f, i) => (
                    <li
                      key={`${f.name}-${i}`}
                      className="flex items-center justify-between px-4 py-3 text-sm"
                    >
                      <span className="flex items-center gap-3 min-w-0">
                        <FileText
                          size={16}
                          strokeWidth={1.5}
                          className="text-primary shrink-0"
                        />
                        <span className="truncate text-primary">
                          {f.name}
                        </span>
                        <span className="text-xs text-muted-foreground shrink-0">
                          {(f.size / 1024 / 1024).toFixed(2)} Mo
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFile(i)}
                        data-testid={`quote-file-remove-${i}`}
                        className="text-muted-foreground hover:text-primary"
                        aria-label={`Retirer ${f.name}`}
                      >
                        <X size={16} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Reassurance line */}
            <div
              data-testid="form-reassurance"
              className="border-t border-border pt-5 text-center"
            >
              <p className="text-[12px] text-primary/80 font-medium flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
                <span>🔒 Vos données sont protégées</span>
                <span className="text-border">·</span>
                <span>Réponse sous 24h</span>
                <span className="text-border">·</span>
                <span>Devis 100% gratuit & sans engagement</span>
              </p>
              <p className="mt-1 text-[11px] text-muted-foreground tracking-wide">
                +100 projets accompagnés sur toute l'île de La Réunion
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 pt-4 border-t border-border">
              <p className="text-xs text-muted-foreground max-w-sm">
                En envoyant ce formulaire, vous acceptez d'être recontacté au
                sujet de votre projet.
              </p>
              <button
                type="submit"
                disabled={loading}
                data-testid="quote-submit"
                className="btn-primary disabled:opacity-60"
              >
                {loading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Send size={16} strokeWidth={1.6} />
                )}
                {loading ? "Envoi..." : "Envoyer ma demande"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, type = "text", required, testId }) {
  return (
    <div>
      <Label
        htmlFor={name}
        className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground"
      >
        {label}
        {required && <span className="text-primary ml-0.5">*</span>}
      </Label>
      <Input
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        type={type}
        required={required}
        data-testid={testId}
        className="mt-2 rounded-none border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary"
      />
    </div>
  );
}
